import { IBaseEntityType, ObjectId } from '../types';
import {
  chain,
  eq,
  get,
  gt,
  head,
  includes,
  isArray,
  isEmpty,
  isFunction,
  isNil,
  isObject,
  join,
  map,
  mergeWith,
  partition,
  reduce,
  size,
  some,
  sortBy,
  toLower,
} from 'lodash';
import Decimal from 'decimal.js';
import {
  DecimalType,
  HybridNumber,
  numberOrString,
} from '../types/common/common.type';
import { NumberFormatService } from '../services/number-format/number-format.service';
import { ValueFormatterParams } from 'ag-grid-community';
import { environment } from '@environments/environment';
import { isDecimalNumber } from '../common/decimal-number/decimal-number.utils';
import { ObjectId as BSONObjectId } from 'bson';

export const isAbsoluteUrl = (
  url: string,
  httpOnly: boolean = true,
): boolean => {
  const regEx = httpOnly
    ? /^(?:https?:)?\/\//
    : /^(?:[a-zA-Z][a-zA-Z\d+.-]*:)?\/\//;

  return regEx.test(url);
};

export const generateObjectId = (): ObjectId =>
  new BSONObjectId().toHexString();

export const partitionAndSortItemsByChargeable = <T extends IBaseEntityType>(
  items: T[],
): T[] =>
  reduce(
    partition(items, 'chargeable'),
    (sortedItems, partitionItems) => [
      ...sortedItems,
      ...sortBy(partitionItems, (item) => toLower(item.name)),
    ],
    [],
  );

export const trackByValue = <T = string>(index, item: { value: T }): T =>
  item.value;

export const hybridCalc = (
  hybridNumbers: Array<HybridNumber | numberOrString>,
): number => {
  if (
    isEmpty(hybridNumbers) ||
    some(hybridNumbers, (hybridNumber: HybridNumber | numberOrString) => {
      const calNumber: numberOrString = isObject(hybridNumber)
        ? hybridNumber.number
        : hybridNumber;
      return !isEffectiveNumber(calNumber);
    })
  ) {
    return null;
  }

  if (eq(size(hybridNumbers), 1)) {
    const hybridNumber = head(hybridNumbers);
    return Number(isObject(hybridNumber) ? hybridNumber.number : hybridNumber);
  }

  const result: number = reduce(
    hybridNumbers,
    (lastCal: number, hybridNumber: HybridNumber | numberOrString): number => {
      const method = isObject(hybridNumber)
        ? hybridNumber?.method
        : DecimalType.PLUS;
      const number = isObject(hybridNumber)
        ? hybridNumber.number
        : hybridNumber;
      return new Decimal(lastCal)[method](Number(number)).toNumber();
    },
    0,
  );
  return result;
};

export const isEffectiveNumber = (number: numberOrString): boolean =>
  !isNaN(parseFloat(String(number))) && isFinite(Number(number));

/**
 * Creates and returns a 2D rendering context for a canvas element.
 * The canvas element is created only once and reused for subsequent calls.
 * @returns {CanvasRenderingContext2D | null} The 2D rendering context, or null if not supported.
 */
export const getCanvasContext = (() => {
  let canvas: HTMLCanvasElement | null = null;
  return (): CanvasRenderingContext2D | null => {
    if (!canvas) {
      canvas = document?.createElement?.('canvas');
    }
    return canvas?.getContext?.('2d');
  };
})();

/**
 * Calculates the rendered width of the text at the specified font size (in pixels)
 * @param text
 * @param fontSize font size（e.g. "14 px" or 14）
 * @param options { fontFamily?: string, fontWeight?: string }
 * @returns textWidth（px）
 */
export const calculateTextWidth = (
  text: string,
  fontSize: string | number,
  options: { fontFamily?: string; fontWeight?: string } = {},
): number => {
  const context = getCanvasContext();
  if (!context) return 0;

  const fontSizeValue =
    typeof fontSize === 'number' ? `${fontSize}px` : fontSize;
  const fontFamily =
    options?.fontFamily || '"Roboto", "ArialMT", Arial, Helvetica, sans-serif';
  const fontWeight = options?.fontWeight || 'normal';
  context.font = `${fontWeight} ${fontSizeValue} ${fontFamily}`;

  return context.measureText(text).width;
};

export const generateTransferUnitText = (
  measure,
  numberFormatService: NumberFormatService,
): string =>
  `${get(measure, 'default_unit.transfer') ? '*' : ''}${get(
    measure,
    'description',
  )}[${numberFormatService.getNumberFormatter()({
    value: measure.factor_value,
  })}]`;

export const quantityNumFormatter = (
  params: ValueFormatterParams,
  numberFormatService: NumberFormatService,
): string =>
  isNil(params.value) ? '' : numberFormatService.getNumberFormatter()(params);

export const mergeWhenNil = <T extends object>(
  original: T,
  override: Partial<T>,
): T =>
  mergeWith(original, override, (objValue, srcValue) =>
    isNil(objValue) ? srcValue : objValue,
  );

export const isXenialUrl = (url: string): boolean => {
  const xenialBaseUrls = [
    environment.xeaBackendURI,
    environment.xviBackendURI,
    environment.xprtBackendURI,
    environment.dmBackendURI,
  ];

  return some(xenialBaseUrls, (sample) => url.indexOf(sample) === 0);
};

export const getNestedProjectPaths = (projectPaths: string[]): string[] => {
  if (isEmpty(projectPaths)) {
    return projectPaths;
  }

  return chain(projectPaths)
    .filter((path) => includes(path, '.'))
    .map((projectPath) =>
      chain(projectPath).split('.').compact().initial().join('.').value(),
    )
    .compact()
    .uniq()
    .value();
};

export const cloneDataWithDepth = (
  data: any,
  depthInitial: number = 5,
): any => {
  let depth: number = depthInitial;
  if (!isObject(data) || isFunction(data) || !depth || depth < 0) return data;

  if (depth === 1) {
    return isArray(data) ? [...data] : { ...data };
  }

  depth = isArray(data) ? depth : depth - 1;

  const cloneFunc = (level: number, obj: any): any => {
    if (!isObject(obj) || isFunction(obj)) return obj;

    const nextLevel = level - 1;
    const nextMethodCall = nextLevel ? cloneFunc.bind(null, nextLevel) : null;

    return isArray(obj)
      ? arrayCloningHandler(obj, nextMethodCall)
      : objectCloningHandler(obj, nextMethodCall);
  };

  return cloneFunc(depth, data);
};

const arrayCloningHandler = (data: any, subFunc: Function): any[] => {
  const newArray = [...data];

  return map(newArray, (item) => (subFunc ? subFunc(item) : item));
};

const objectCloningHandler = (data: any, subFunc?: Function): any => {
  const newObj = { ...data };

  for (const key in newObj) {
    if (
      !newObj.hasOwnProperty(key) ||
      isDecimalNumber(newObj[key]) || // should think decimal number as pr obj
      !isObject(newObj[key]) ||
      isFunction(newObj[key])
    ) {
      continue;
    }
    if (isArray(newObj[key])) {
      newObj[key] = subFunc
        ? map(newObj[key], (item) => subFunc(item))
        : [...newObj[key]];
    } else {
      newObj[key] = subFunc ? subFunc({ ...newObj[key] }) : { ...newObj[key] };
    }
  }

  return newObj;
};

export const getExtraQuickFilterText = ({
  name,
  extraValues = [],
}: {
  name: string;
  extraValues?: string[];
}): string => {
  let quickFilterText = '';

  if (!isEmpty(name)) {
    quickFilterText = `${name} `;
  }

  if (gt(size(extraValues), 0)) {
    quickFilterText += join(extraValues, '');
  }

  return quickFilterText;
};
